import { ChevronLeft, Hash } from "lucide-react";
import type { GetStaticPaths, GetStaticProps, NextPage } from "next";
import Head from "next/head";
import Link from "next/link";
import { ParsedUrlQuery } from "querystring";
import { BlogList } from "components/molecules/BlogList";
import { MDXFrontMatter } from "types";
import { getAllMdx } from "utils/mdx";
import { slugify } from "utils/utils";
interface ContextProps extends ParsedUrlQuery {
  tag: string;
}
interface BlogPostsProps {
  tag: string;
  blogs: Array<MDXFrontMatter>;
  popularTags: Array<{
    tag: string;
    count: number;
  }>;
}
const TaggedBlogs: NextPage<BlogPostsProps> = ({
  tag,
  blogs,
  popularTags
}) => {
  return <>
            <Head data-sentry-element="Head" data-sentry-source-file="[tag].tsx">
                <title>
                    CharGen: AI Generated RPG Character Generator Blogs Tagged:{" "}
                    {tag}
                </title>
                <meta name="description" content={`CharGen Blogs tagged ${tag}: AI Generated Art for your D&D Characters within seconds. Create stunning D&D Characters art for free with CharGen. No prompting or AI knowledge needed!`} data-sentry-element="meta" data-sentry-source-file="[tag].tsx" />
                <meta property="og:url" key="og:url" content={`https://char-gen.com/blogs/tagged/${tag}`} data-sentry-element="meta" data-sentry-source-file="[tag].tsx" />
                <meta property="og:description" key="og:description" content={`CharGen Blogs tagged ${tag}: AI Generated Art for your D&D Characters within seconds. Create stunning D&D Characters art for free with CharGen. No prompting or AI knowledge needed!`} data-sentry-element="meta" data-sentry-source-file="[tag].tsx" />
                <link rel="canonical" href={`https://char-gen.com/blogs/tagged/${tag}`} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "CharGen",
          url: `https://char-gen.com/blogs/tagged/${tag}`,
          description: `CharGen Blogs tagged ${tag}: AI Generated Art for your D&D Characters within seconds. Create stunning D&D Characters art for free with CharGen. No prompting or AI knowledge needed!`
        })
      }} />
            </Head>
            <div className="bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 min-h-screen">
                {/* Hero section with tag highlight */}
                <div className="relative overflow-hidden">
                    <div className="absolute inset-0 bg-slate-900 opacity-90"></div>
                    <div className="absolute inset-0 bg-gradient-to-br from-sky-900/30 via-slate-900/0 to-purple-900/20"></div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
                        <div className="text-center md:text-left">
                            <div className="inline-flex items-center mb-3 px-3 py-1 rounded-full bg-slate-800/70 text-sky-400 border border-sky-700/30">
                                <Hash className="h-4 w-4 mr-2" data-sentry-element="Hash" data-sentry-source-file="[tag].tsx" />
                                <span>Tagged</span>
                            </div>

                            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight capitalize">
                                {tag}
                            </h1>

                            <p className="text-lg text-slate-300 max-w-2xl mb-8">
                                Exploring all articles related to {tag} in RPG
                                character creation, tabletop gaming, and AI art
                                generation.
                            </p>

                            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                                <Link href="/blogs" className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium 
                                    rounded-lg inline-flex items-center transition-colors border border-slate-700" data-sentry-element="Link" data-sentry-source-file="[tag].tsx">
                                    <ChevronLeft className="w-4 h-4 mr-1" data-sentry-element="ChevronLeft" data-sentry-source-file="[tag].tsx" />
                                    All Articles
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Popular tags section */}
                <div className="border-t border-slate-800 py-8">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-6">
                            <h2 className="text-xl font-bold text-white mb-2">
                                Related Topics
                            </h2>
                        </div>

                        <div className="flex flex-wrap justify-center gap-3">
                            {popularTags.map(({
              tag: tagName,
              count
            }) => <Link key={tagName} href={`/blogs/tagged/${slugify(tagName)}`} className={`px-4 py-2 rounded-full inline-flex items-center transition-colors shadow-md ${tagName === tag ? "bg-sky-600 text-white" : "bg-slate-800 hover:bg-slate-700 text-slate-200"}`}>
                                    <Hash className="w-4 h-4 mr-1" />
                                    {tagName}
                                    <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${tagName === tag ? "bg-sky-700 text-sky-100" : "bg-slate-700 text-slate-300"}`}>
                                        {count}
                                    </span>
                                </Link>)}
                        </div>
                    </div>
                </div>

                {/* Main blog content */}
                <div className="py-12">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <BlogList blogs={blogs} data-sentry-element="BlogList" data-sentry-source-file="[tag].tsx" />
                    </div>
                </div>
            </div>
        </>;
};
export const getStaticPaths: GetStaticPaths = async () => {
  // Retrieve and filter the MDX files, ensuring only defined frontMatter is processed
  const mdxFiles = getAllMdx().map((blog: {
    frontMatter: MDXFrontMatter;
    content: string;
  } | null) => blog?.frontMatter).filter((frontMatter): frontMatter is MDXFrontMatter => frontMatter !== undefined);

  // Extract unique tags from the filtered frontMatter
  const allTags = new Set(mdxFiles.map(file => file.tags).flat().filter((tag): tag is string => tag !== undefined));

  // Map unique tags to paths
  const paths = Array.from(allTags).map(tag => ({
    params: {
      tag: slugify(tag)
    }
  }));
  return {
    paths,
    fallback: false
  };
};
export const getStaticProps: GetStaticProps = async context => {
  const {
    tag
  } = context.params as ContextProps;
  // Retrieve and filter the MDX files, ensuring only defined frontMatter is processed
  const mdxFiles = getAllMdx().map((blog: {
    frontMatter: MDXFrontMatter;
    content: string;
  } | null) => blog?.frontMatter).filter((frontMatter): frontMatter is MDXFrontMatter => frontMatter !== undefined);

  // Filter blogs by the provided tag
  const filteredBlogs = mdxFiles.filter(file => file.tags?.includes(tag));

  // Extract and count all tags
  const tagCounts: Record<string, number> = {};
  mdxFiles.forEach(blog => {
    if (blog.tags) {
      blog.tags.forEach(tagName => {
        tagCounts[tagName] = (tagCounts[tagName] || 0) + 1;
      });
    }
  });

  // Get the most popular tags
  const popularTags = Object.entries(tagCounts).map(([tagName, count]) => ({
    tag: tagName,
    count
  })).sort((a, b) => b.count - a.count).slice(0, 8);
  return {
    props: {
      tag,
      blogs: filteredBlogs,
      popularTags
    }
  };
};
export default TaggedBlogs;